import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { useResizeDetector } from 'react-resize-detector';
import clsx from 'clsx';

import { withStyling, IInjectedStylingProps, StylingOverrideUtil } from './higher-order';

import styles from './modal.module.scss';

export interface IModalProps extends IInjectedStylingProps {
	children?: React.JSX.Element;
	isOpen: boolean;
	onClose: () => void;
	title?: string | React.JSX.Element;
	footerButtons?: IModalFooter;
	customFooter?: React.JSX.Element;
	maxWidth?: number;
	isLoading?: boolean;
	closeIcon?: React.JSX.Element | false;
	bodyPadding?: [number, number];
	className?: string;
}

export interface IModalFooter {
	cancelButton?: React.JSX.Element;
	submitButton?: React.JSX.Element;
	dangerButton?: React.JSX.Element;
}

export interface IModalButton {
	onClick?: () => void;
	title: string;
	isLoading?: boolean;
	disabled?: boolean;
	variant?: string;
}

const MODAL_PADDING = 16;
const BODY_PADDING = 16;

const Wrapper = (props: IModalProps): React.ReactPortal | React.JSX.Element => {
	if (typeof document === 'undefined' || !props.isOpen) return <></>;

	return <InnerModal {...props} />;
};

const InnerModal = (props: IModalProps): React.ReactPortal => {
	const footerRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const [bodyMaxCssHeight, setBodyMaxCssHeight] = useState<string>();
	const [bodyMaxHeight, setBodyMaxHeight] = useState<number>(0);
	const [bodyRefHeightWithPadding, setBodyRefHeightWithPadding] = useState<number>(0);
	const { height: bodyRefHeight, ref: bodyRef } = useResizeDetector();
	const { height: modalRefHeight, ref: modalRef } = useResizeDetector();

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		const scrollbarSize = window.innerWidth - document.documentElement.clientWidth;
		if (typeof window !== 'undefined') {
			window.addEventListener('keydown', handleEscapeKey);
			document.body.style.overflow = 'hidden';
			document.body.style.paddingRight = `${scrollbarSize}px`;

			return (): void => {
				window.removeEventListener('keydown', handleEscapeKey);
				document.body.style.overflow = 'auto';
				document.body.style.paddingRight = '0px';
			};
		}
	}, []);

	const handleEscapeKey = (e: KeyboardEvent): void => {
		if (e.key === 'Escape') {
			props.onClose();
		}
	};

	useEffect(() => {
		const bodyPadding = props.bodyPadding?.[0] ?? BODY_PADDING;
		setBodyRefHeightWithPadding(bodyRefHeight || 0 + bodyPadding * 2);
	}, [bodyRefHeight, props.bodyPadding]);

	useEffect(() => {
		const headerHeight = headerRef.current?.clientHeight || 0;
		const footerHeight = footerRef.current?.clientHeight || 0;
		const parentHeight = modalRefHeight ? `${modalRefHeight}px` : '100vh';

		setBodyMaxCssHeight(`calc(${parentHeight} - ${headerHeight}px - ${footerHeight}px)`);
		if (typeof window !== 'undefined') {
			setBodyMaxHeight((modalRefHeight || window.innerHeight) - headerHeight - footerHeight);
		}
	}, [headerRef, footerRef, bodyRefHeight, props.bodyPadding, modalRefHeight]);

	return ReactDOM.createPortal(
		<FocusLock autoFocus={false}>
			<div
				ref={modalRef}
				className={clsx(props.styling?.getStyle(styles, 'wrapper'), props.className)}
				style={{
					padding: `${MODAL_PADDING}px`
				}}
			>
				<div
					role="button"
					tabIndex={-1}
					aria-label="closeOverlay"
					onClick={() => {
						if (!props.isLoading) {
							props.onClose();
						}
					}}
					className={props.styling?.getStyle(styles, 'overlay')}
				/>
				<div
					className={props.styling?.getStyle(styles, 'modalDialog')}
					role="document"
					style={{
						maxWidth: props.maxWidth ? `${props.maxWidth}px` : '500px'
					}}
				>
					<div className={props.styling?.getStyle(styles, 'modalContent')}>
						<div
							ref={headerRef}
							className={clsx(
								props.styling?.getStyle(styles, 'header'),
								typeof props.title === 'string' && props.styling?.getStyle(styles, 'noJsxHeader')
							)}
						>
							{typeof props.title === 'string' ? <h2>{props.title}</h2> : props.title}
							{props.closeIcon !== false && (
								<button
									onClick={props.onClose}
									className={props.styling?.getStyle(styles, 'close')}
									type="button"
									disabled={props.isLoading}
								>
									{props.closeIcon || 'x'}
								</button>
							)}
						</div>
						<div
							className={props.styling?.getStyle(styles, 'body')}
							style={{
								maxHeight: bodyMaxCssHeight || '50vh',
								overflow: (bodyRefHeightWithPadding || 0) + MODAL_PADDING * 2 >= bodyMaxHeight ? 'auto' : 'visible'
							}}
						>
							<div
								className={props.styling?.getStyle(styles, 'measure')}
								ref={bodyRef}
								style={{
									padding: props.bodyPadding ? `${props.bodyPadding[0]}px ${props.bodyPadding[1]}px` : BODY_PADDING
								}}
							>
								{props.children}
							</div>
						</div>
						{(props.footerButtons || props.customFooter) && (
							<div className={props.styling?.getStyle(styles, 'footer')} ref={footerRef}>
								{props.customFooter ??
									renderFooter(
										props.onClose,
										props.styling,
										props.isLoading,
										props.footerButtons?.cancelButton,
										props.footerButtons?.submitButton,
										props.footerButtons?.dangerButton
									)}
							</div>
						)}
					</div>
				</div>
			</div>
		</FocusLock>,
		document.body
	);
};

const Modal = withStyling(Wrapper);
export { Modal };

const renderFooter = (
	onClose: () => void,
	styling?: StylingOverrideUtil,
	isLoading?: boolean,
	cancel?: React.JSX.Element,
	submit?: React.JSX.Element,
	danger?: React.JSX.Element
): React.JSX.Element => {
	if (!cancel && !submit && !danger) return <></>;

	const cancelButtonElement = cancel ? (
		React.cloneElement(cancel, {
			...cancel.props,
			onClick: cancel.props.onClick ?? onClose,
			disabled: isLoading
		})
	) : (
		<></>
	);

	const submitButtonElement = submit ? React.cloneElement(submit, { ...submit.props, isLoading }) : <></>;

	const dangerButtonElement = danger ? (
		React.cloneElement(danger, {
			...danger.props,
			disabled: isLoading
		})
	) : (
		<></>
	);

	return (
		<div className={styling?.getStyle(styles, 'buttons')}>
			<div className={styling?.getStyle(styles, 'buttonsLeft')}>{dangerButtonElement}</div>
			<div className={styling?.getStyle(styles, 'buttonsRight')}>
				{cancelButtonElement}
				{submitButtonElement}
			</div>
		</div>
	);
};
